import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, apiError } from "../context/AuthContext";
import { api } from "../lib/api";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Loader2, KeyRound } from "lucide-react";
import ThemeToggle from "../components/ThemeToggle";

const LOCAL_DEMO_ACCOUNTS = [
  { id: "admin", label: "Amministratore" },
  { id: "fleet_manager", label: "Fleet Manager" },
  { id: "operator", label: "Operatore" },
  { id: "driver", label: "Autista" },
];

export default function Login() {
  const LOCAL_DEMO = process.env.REACT_APP_LOCAL_DEMO === "true";
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [demoAccounts, setDemoAccounts] = useState([]);
  const [demoError, setDemoError] = useState(false);

  useEffect(() => {
    if (!LOCAL_DEMO) return;
    const controller = new AbortController();
    api.get("/local-demo/accounts", { signal: controller.signal })
      .then(({ data }) => {
        if (controller.signal.aborted) return;
        const complete = Array.isArray(data) && LOCAL_DEMO_ACCOUNTS.every((profile) =>
          data.some((account) => account.role === profile.id && typeof account.email === "string"
            && account.email && typeof account.password === "string" && account.password));
        if (!complete) throw new Error("Account demo non disponibili");
        setDemoAccounts(data);
      })
      .catch(() => { if (!controller.signal.aborted) setDemoError(true); });
    return () => controller.abort();
  }, [LOCAL_DEMO]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Accesso effettuato");
      navigate("/");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };
  // Without HTTPS the browser disables crypto.subtle and navigator.locks: every save would fail.
  const insecure = typeof window !== "undefined" && window.isSecureContext === false;
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="grid w-full max-w-5xl overflow-hidden rounded-2xl border border-border bg-card lg:grid-cols-2">
        {/* Left brand panel */}
        <div className="relative hidden flex-col justify-between login-brand p-10 lg:flex">
          <Logo onDark />
          <div>
            <h2 className="font-heading text-4xl font-extrabold leading-tight tracking-tight text-white">
              Gestione flotta<br />semplice e completa.
            </h2>
            <p className="mt-4 max-w-sm text-white/75">
              Monitora veicoli, assegnazioni, manutenzioni, scadenze e costi della flotta AutoTu in un'unica piattaforma.
            </p>
          </div>
          <p className="text-xs text-white/60">© {new Date().getFullYear()} AutoTu · Fleet Management</p>
        </div>

        {/* Right form */}
        <div className="relative p-8 sm:p-10">
          <ThemeToggle className="absolute right-3 top-3" />
          <div className="mb-8 lg:hidden"><Logo /></div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Accedi</h1>
          <p className="mb-6 text-sm text-muted-foreground">Inserisci le tue credenziali per continuare</p>
          {insecure && <p role="alert" className="mb-6 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm" data-testid="insecure-context">
            Connessione non sicura (HTTP). Il browser disattiva le funzioni necessarie al salvataggio: potrai consultare i dati ma nessuna modifica verrà salvata. Apri il gestionale con un indirizzo HTTPS.
          </p>}

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="mb-1.5 block">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={LOCAL_DEMO ? "Email demo" : "nome@autotu.it"} required data-testid="login-email" />
            </div>
            <div>
              <Label htmlFor="password" className="mb-1.5 block">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required data-testid="login-password" />
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
              <span className="inline-flex" aria-hidden="true">
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />}
              </span>
              <span>Accedi</span>
            </Button>
          </form>
          <Link to="/forgot-password" className="mt-3 inline-flex min-h-11 items-center text-sm font-medium text-primary hover:underline">Password dimenticata?</Link>

          {LOCAL_DEMO && (
            <div className="mt-8" data-testid="local-login-instructions">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Accesso demo rapido</p>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Seleziona un profilo per compilare email e password, poi premi Accedi.
              </p>
              <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {LOCAL_DEMO_ACCOUNTS.flatMap(profile => {
                  const matching=demoAccounts.filter(item=>item.role===profile.id);
                  return (matching.length?matching:[null]).map((account,index) => (
                    <button key={account?.email || profile.id} type="button" disabled={!account}
                      onClick={() => { setEmail(account.email); setPassword(account.password); }}
                      aria-pressed={Boolean(account) && email === account.email}
                      data-testid={`local-demo-${profile.id}${index ? `-${index+1}` : ''}`}
                      className="min-h-11 rounded-lg border border-border bg-secondary/40 px-3 py-2 text-left text-xs transition-colors hover:border-primary/50 hover:bg-secondary aria-pressed:border-primary aria-pressed:bg-primary/10 disabled:opacity-50">
                      <span className="block font-medium text-foreground">{profile.label}{matching.length>1?` ${index+1}`:''}</span>
                      <span className="mt-1 block break-all text-muted-foreground">{account?.email || (demoError ? "Account non disponibile" : "Caricamento account…")}</span>
                    </button>
                  ));
                })}
              </div>
              {demoError && <p className="mt-3 text-sm text-destructive" role="alert">
                Accessi demo non disponibili. Ricarica la pagina oppure inserisci le credenziali del file privato .local-dev/access.txt.
              </p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
